import React, { useState, useEffect, useMemo } from 'react';

import { downloadUrl } from '@apis/common';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogTitle,
  ActionableDialogContent,
  ActionableDialogActions,
} from '@components/Dialog';
import { RadioGroupInput } from '@components/Form';
import { useCommonStyles } from '@styles/index';
import { createValidate, formStrings, requiredStringField } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { Formik, Form, useFormikContext } from 'formik';
import * as t from 'io-ts';
import { stringify } from 'query-string';

const strings = createStrings({
  ja: {
    exportMethod: 'エクスポート手段',
    title: (subject: string) => `${subject}をエクスポートする`,
    selected: (subject: string, selectedQuantity: number) =>
      `選択済み: ${selectedQuantity}個の${subject}`,
    matchesSearchResults: (subject: string) => `検索内容と一致している${subject}`,
    all: (subject: string) => `すべての${subject}`,
  },
});

const SubmitButton: React.FC<{ subject: string }> = ({ subject }) => {
  const [disabled, setDisabled] = useState(false);
  const { values } = useFormikContext<ExportFormValues>();

  useEffect(() => {
    setDisabled(values.method == null);
  }, [values, setDisabled]);

  return (
    <Button
      disabled={disabled}
      color="primary"
      size="small"
      text={formStrings.export(subject)}
      type="submit"
    />
  );
};

export const ExportCSVDialog: React.FC<ExportDialogProps> = (props) => {
  const {
    subject,
    open,
    onClose,
    query = {},
    selectedIds,
    baseUrl,
    useMatch = true,
    defaultQuery = {},
  } = props;

  const classes = {
    ...useCommonStyles(),
  };

  const schema = useMemo(() => {
    return t.type({
      method: requiredStringField(strings.exportMethod),
    });
  }, []);

  const validate = useMemo(() => createValidate(schema), [schema]);

  const submit = async (values: ExportFormValues) => {
    const { method } = values;

    const params = (() => {
      switch (method) {
        case 'matches':
          return query;
        case 'selected':
          return {
            selected: selectedIds,
          };
        default:
          return defaultQuery;
      }
    })();

    const finalParams = {
      ...params,
      page: undefined,
      page_size: undefined,
      format: 'csv',
    };

    await downloadUrl({
      url: `${baseUrl}?${stringify(finalParams)}`,
      name: subject,
      type: 'text/csv',
    });
  };

  const selectedQuantity = selectedIds.length;

  return (
    <Formik
      validate={validate}
      initialValues={{}}
      onSubmit={async (values) => {
        try {
          await submit(values);
          onClose();
        } catch (e) {
          alert(e);
        }
      }}
    >
      {(formikProps) => {
        return (
          <Form>
            <ActionableDialog open={open} onClose={onClose}>
              <ActionableDialogTitle>{strings.title(subject)}</ActionableDialogTitle>
              <ActionableDialogContent className={clsx(classes.textGray700, classes.middleText)}>
                <RadioGroupInput
                  name="method"
                  label=""
                  noLabel
                  formControlLabelClasses={{
                    label: classes.middleTextImportant,
                  }}
                  options={[
                    {
                      label: strings.selected(subject, selectedQuantity),
                      value: 'selected',
                      disabled: selectedQuantity === 0,
                    },
                    ...(useMatch
                      ? [
                          {
                            label: strings.matchesSearchResults(subject),
                            value: 'matches',
                          },
                        ]
                      : []),
                    {
                      label: strings.all(subject),
                      value: 'all',
                    },
                  ]}
                />
              </ActionableDialogContent>
              <ActionableDialogActions>
                <Button
                  color="printable"
                  size="small"
                  text={formStrings.cancel}
                  onClick={() => {
                    formikProps.resetForm();
                    onClose();
                  }}
                  type="button"
                />
                <SubmitButton subject={subject} />
              </ActionableDialogActions>
            </ActionableDialog>
          </Form>
        );
      }}
    </Formik>
  );
};

export interface ExportFormValues {
  method?: 'selected' | 'matches' | 'all';
}

export interface ExportDialogProps {
  open: boolean;
  onClose: () => any;
  subject: string;
  useMatch?: boolean;
  selectedIds: number[] | string[];
  baseUrl: string;
  query?: Record<string, any>;
  defaultQuery?: Record<string, any>;
}
